import { IMMUNISATION_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import type { DateArg } from "date-fns";
import { router } from "expo-router";
import { useEffect, useId, useState } from "react";

import { Button } from "~/components/Button";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { DatePicker } from "~/components/form/date-picker/DatePicker";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Space } from "~/constants/sizing";
import type { Immunisation } from "~/entities/medical/immunisation";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { elementsMatch } from "~/util/array";

type ImmunisationFormProps = {
    readonly immunisation?: Immunisation;
    onRequestClose: () => void;
} & FullPageModalProps;

export const ImmunisationForm = ({ immunisation, onRequestClose, ...modalProps }: ImmunisationFormProps) => {
    const { canUpdateMedical, storeMedical } = useMedical();
    const { saving, setSaving } = useFeedback();
    const [option, setOption] = useState<string | null>(immunisation?.option_id ?? null);
    const [immunisationDate, setImmunisationDate] = useState<DateArg<Date> | null>(
        immunisation?.immunisation_date ?? null,
    );
    const [description, setDescription] = useState(immunisation?.description ?? "");
    const [photographs, setPhotographs] = useState<string[]>(immunisation?.photograph_ids ?? []);
    const [dirty, setDirty] = useState(false);
    const id = useId();

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (immunisation?.option_id ?? null) ||
                immunisationDate !== (immunisation?.immunisation_date ?? null) ||
                description !== (immunisation?.description ?? "") ||
                !elementsMatch(photographs, immunisation?.photograph_ids ?? []),
        );
    }, [immunisation, option, description, immunisationDate, photographs]);

    const canSave =
        canUpdateMedical(MedicalType.immunisation) && !saving && option !== null && immunisationDate !== null;

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        storeMedical({
            type: MedicalType.immunisation,
            id: immunisation?.id,
            option_id: option,
            photograph_ids: photographs,
            immunisation_date: immunisationDate,
            description,
        })
            .then((id) => {
                id &&
                    setTimeout(
                        () => router.dismissTo({ pathname: `${IMMUNISATION_BASE_URL}/[id]/show`, params: { id } }),
                        10,
                    );
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            title={`${immunisation?.id ? "Edit" : "Add"} immunisation`}
            cancelable
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
            gap={Space.lg}
        >
            <FieldGroup>
                <OptionSelector
                    type={OptionType.immunisations}
                    value={option}
                    onItemSelected={setOption}
                    label="Immunisation"
                    disabled={is_saving}
                    name="option_id"
                />
                <DatePicker
                    value={immunisationDate}
                    onDateSelected={setImmunisationDate}
                    label="Date of vaccination"
                    placeholder="Select date"
                    name="immunisation_date"
                />
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    value={description}
                    onChangeText={setDescription}
                    label="Description"
                    optional
                    disabled={is_saving}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
